import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, MantineColor} from '@mantine/core';
import React, {AriaRole, ReactNode} from 'react';

interface HomepageBannerProps {
  children: ReactNode;
  color: MantineColor;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  role?: AriaRole;
  'aria-describedby'?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  dataTestAttrs?: DataTestAttrs;
}

const useStyles = createStyles((theme, {color}: {color: MantineColor}) => ({
  banner: {
    backgroundColor: theme.colors[color][1],
    borderLeft: `4px solid ${theme.colors[color][7]}`,
    borderRadius: theme.radius.sm,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing.sm,
    margin: '0 auto',
    gap: theme.spacing.md,
    width: '100%',
  },
  flexGrow: {
    flexGrow: 1,
  },
}));

export default function HomepageBanner({
  children,
  color,
  leftSection,
  rightSection,
  role,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  dataTestAttrs,
}: HomepageBannerProps): JSX.Element {
  const {classes} = useStyles({color});

  return (
    <Box
      className={classes.banner}
      role={role}
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      {...getTestProps(dataTestAttrs)}
    >
      {leftSection}
      {children}
      {rightSection != null && (
        <>
          <div className={classes.flexGrow} />
          {rightSection}
        </>
      )}
    </Box>
  );
}
